import {assert, isEmpty} from '@pexip/utils';

import type {
    ProcessorOptions,
    ProcessStatus,
    RendererOptions,
} from '../../common';
import {
    BACKGROUND_BLUR_AMOUNT,
    BACKGROUND_THRESHOLD,
    DOWN_SAMPLE_FACTOR,
    EDGE_BLUR_AMOUNT,
    EXCLUDE_BYSTANDERS,
    FOREGROUND_THRESHOLD,
    LIGHT_WRAP_BLUR_AMOUNT,
    LIGHT_WRAP_EDGE_BAND,
    LIGHT_WRAP_INTENSITY,
    LIGHT_WRAP_TIGHTNESS,
    MASK_COMBINE_RATIO,
    MORPH_DILATE_RADIUS_PX,
    MORPH_ERODE_RADIUS_PX,
    MORPH_PASS,
    PERSON_CENTER,
    PROCESSING_HEIGHT,
    PROCESSING_WIDTH,
    SIGMA_RANGE,
    SIGMA_SPACE,
} from '../../common/constants';
import {
    clamping,
    createObjectUpdater,
    compareImageRecords,
} from '../../common/utils';
import type {Segmenter, InputProcessAdaptor} from './types';
import {assertMediaStreamVideoTrack, isRenderEffects} from './typeGuards';

export interface ProcessorUpdateOptions
    extends Omit<ProcessorOptions, 'renderOptions'> {
    renderOptions?: Partial<RendererOptions>;
}

export interface VideoProcessor {
    readonly status: ProcessStatus;
    /**
     * Initialize the video processor and related
     */
    open(options?: ProcessorUpdateOptions): Promise<void>;
    /**
     * Update the video processor with provided options
     */
    update(options: ProcessorUpdateOptions): Promise<void>;
    mute(muted: boolean): void;
    /**
     * Process the video and return a MediaStream
     */
    process: (source: MediaStreamTrack) => Promise<MediaStreamTrack>;
    /**
     * Stop the process of video segmentation
     */
    close(): Promise<void>;
    /**
     * Destroy the segmentation wasm instance
     */
    destroy: () => Promise<void>;
    /**
     * Current renderer options
     */
    readonly renderOptions: RendererOptions;
    /**
     * Current processing width
     */
    readonly processingWidth: number;
    /**
     * Current processing height
     */
    readonly processingHeight: number;
}

interface Props {
    processing: boolean;
    track?: MediaStreamTrack;
    outputTrack?: MediaStreamTrack;
}

const clampInRangeFrom0To1 = clamping(0, 1);
const clampInRangeFrom0To9 = clamping(0, 9);

type RendererOptionHandler = <K extends keyof RendererOptions>(
    key: K,
    value: RendererOptions[K],
) => void;

const createRenderOptions = (
    {
        foregroundThreshold = FOREGROUND_THRESHOLD,
        backgroundThreshold = BACKGROUND_THRESHOLD,
        backgroundBlurAmount = BACKGROUND_BLUR_AMOUNT,
        edgeBlurAmount = EDGE_BLUR_AMOUNT,
        maskCombineRatio = MASK_COMBINE_RATIO,
        backgroundImageUrl = '',
        sigmaSpace = SIGMA_SPACE,
        sigmaRangeLo = SIGMA_RANGE[0],
        sigmaRangeHi = SIGMA_RANGE[1],
        effects = 'none',
        backend = 'webgl',
        personCenter = PERSON_CENTER,
        excludeBystanders = EXCLUDE_BYSTANDERS,
        morphPass = MORPH_PASS,
        morphErodeRadiusPx = MORPH_ERODE_RADIUS_PX,
        morphDilateRadiusPx = MORPH_DILATE_RADIUS_PX,
        lightWrapIntensity = LIGHT_WRAP_INTENSITY,
        downSampleFactor = DOWN_SAMPLE_FACTOR,
        lightWrapTightness = LIGHT_WRAP_TIGHTNESS,
        lightWrapBlurAmount = LIGHT_WRAP_BLUR_AMOUNT,
        lightWrapEdgeBand = LIGHT_WRAP_EDGE_BAND,
    }: Partial<ProcessorOptions['renderOptions']> = {},
    onChange: RendererOptionHandler,
) => {
    const props: RendererOptions = {
        foregroundThreshold,
        backgroundThreshold,
        backgroundBlurAmount,
        edgeBlurAmount,
        maskCombineRatio,
        backgroundImageUrl,
        sigmaSpace,
        sigmaRangeLo,
        sigmaRangeHi,
        effects,
        backend,
        personCenter,
        excludeBystanders,
        morphPass,
        morphErodeRadiusPx,
        morphDilateRadiusPx,
        lightWrapIntensity,
        downSampleFactor,
        lightWrapTightness,
        lightWrapBlurAmount,
        lightWrapEdgeBand,
    };
    return {
        original: props,
        proxy: new Proxy<RendererOptions>(props, {
            get(target, p: keyof RendererOptions, receiver) {
                return Reflect.get(target, p, receiver);
            },
            set(target, p: keyof RendererOptions, newValue, receiver) {
                switch (p) {
                    case 'backgroundThreshold': {
                        const value = clamping(
                            0,
                            target.foregroundThreshold,
                        )(newValue);
                        if (value !== target[p]) {
                            Reflect.set(target, p, value, receiver);
                            onChange(p, value);
                        }
                        break;
                    }
                    case 'foregroundThreshold': {
                        const value = clamping(
                            target.backgroundThreshold,
                            1,
                        )(newValue);
                        if (value !== target[p]) {
                            Reflect.set(target, p, value, receiver);
                            onChange(p, value);
                        }
                        break;
                    }
                    case 'maskCombineRatio': {
                        const value = clampInRangeFrom0To1(newValue);
                        if (value !== target[p]) {
                            Reflect.set(target, p, value, receiver);
                            onChange(p, value);
                        }
                        break;
                    }
                    case 'lightWrapBlurAmount':
                    case 'backgroundBlurAmount':
                    case 'edgeBlurAmount': {
                        // Expecting integer number
                        const value = Math.trunc(
                            clampInRangeFrom0To9(newValue),
                        );
                        if (value !== target[p]) {
                            Reflect.set(target, p, value, receiver);
                            onChange(p, value);
                        }
                        break;
                    }
                    case 'backgroundImageUrl': {
                        if (newValue !== target[p]) {
                            Reflect.set(target, p, newValue, receiver);
                            onChange(p, newValue);
                        }
                        break;
                    }
                    case 'effects': {
                        if (
                            isRenderEffects(newValue) &&
                            newValue !== target[p]
                        ) {
                            Reflect.set(target, p, newValue, receiver);
                            onChange(p, newValue);
                        }
                        break;
                    }
                    case 'backend': {
                        if (
                            (newValue === 'webgl' || newValue === 'webgpu') &&
                            newValue !== target[p]
                        ) {
                            Reflect.set(target, p, newValue, receiver);
                            onChange(p, newValue);
                        }
                        break;
                    }
                    case 'sigmaSpace': {
                        // less than 0 is unknown behavior, more than 12 is too much
                        const value = clamping(0, 12)(newValue);
                        if (value !== target.sigmaSpace) {
                            Reflect.set(target, p, value, receiver);
                            onChange(p, value);
                        }
                        break;
                    }
                    case 'sigmaRangeLo': {
                        const value = clamping(
                            0,
                            target.sigmaRangeHi,
                        )(newValue);
                        if (value !== target.sigmaRangeLo) {
                            Reflect.set(target, p, value, receiver);
                            onChange(p, value);
                        }
                        break;
                    }
                    case 'sigmaRangeHi': {
                        const value = clamping(
                            target.sigmaRangeLo,
                            1,
                        )(newValue);
                        if (value !== target.sigmaRangeHi) {
                            Reflect.set(target, p, value, receiver);
                            onChange(p, value);
                        }
                        break;
                    }
                    case 'personCenter': {
                        if (Array.isArray(newValue)) {
                            const [x, y] = (newValue as [number, number]).map(
                                clampInRangeFrom0To1,
                            );
                            if (
                                x !== undefined &&
                                y !== undefined &&
                                (x !== target.personCenter[0] ||
                                    y !== target.personCenter[1])
                            ) {
                                Reflect.set(target, p, [x, y], receiver);
                                onChange(p, [x, y]);
                            }
                        }
                        break;
                    }
                    case 'excludeBystanders': {
                        if (newValue !== target[p]) {
                            Reflect.set(target, p, newValue, receiver);
                            onChange(p, newValue);
                        }
                        break;
                    }
                    case 'morphDilateRadiusPx':
                    case 'morphErodeRadiusPx': {
                        const value = clamping(0, 12)(newValue);
                        if (value !== target[p]) {
                            Reflect.set(target, p, value, receiver);
                            onChange(p, value);
                        }
                        break;
                    }
                    case 'morphPass': {
                        // Expecting integer number
                        const value = Math.trunc(clamping(0, 10)(newValue));
                        if (value !== target[p]) {
                            Reflect.set(target, p, value, receiver);
                            onChange(p, value);
                        }
                        break;
                    }
                    case 'lightWrapIntensity': {
                        const value = clampInRangeFrom0To1(newValue);
                        if (value !== target[p]) {
                            Reflect.set(target, p, value, receiver);
                            onChange(p, value);
                        }
                        break;
                    }
                    case 'lightWrapTightness': {
                        // According to the doc
                        const value = clamping(0.5, 4)(newValue);
                        if (value !== target[p]) {
                            Reflect.set(target, p, value, receiver);
                            onChange(p, value);
                        }
                        break;
                    }
                    case 'lightWrapEdgeBand': {
                        const value = clamping(
                            0,
                            (target.foregroundThreshold -
                                target.backgroundThreshold) /
                                2,
                        )(newValue);
                        if (value !== target[p]) {
                            Reflect.set(target, p, value, receiver);
                            onChange(p, value);
                        }
                        break;
                    }
                    case 'downSampleFactor': {
                        // NOTE: There is no impl other than 2 and 4, so we just hardcode it here
                        if (
                            (newValue === 2 || newValue === 4) &&
                            newValue !== target[p]
                        ) {
                            Reflect.set(target, p, newValue, receiver);
                            onChange(p, newValue);
                        }
                        break;
                    }
                    default: {
                        // Catch all the unknown keys, and just ignore the set operation
                        return false;
                    }
                }
                // No throw
                return true;
            },
        }),
    };
};

export const createVideoProcessor = (
    segmenter: Segmenter,
    inputProcessAdaptor: InputProcessAdaptor,
): VideoProcessor => {
    const canUpdate = () => segmenter.status !== 'new';

    const props: Props = {
        processing: false,
    };
    let changedRenderOptions: Partial<RendererOptions> = {};

    const initRenderOptions = () =>
        createRenderOptions({}, (k, v) => {
            changedRenderOptions[k] = v;
        });
    let renderOptions = initRenderOptions();
    let processorOptions: ProcessorOptions &
        Required<
            Pick<
                ProcessorOptions,
                'renderOptions' | 'processingWidth' | 'processingHeight'
            >
        > = {
        renderOptions: renderOptions.original,
        processingWidth: PROCESSING_WIDTH,
        processingHeight: PROCESSING_HEIGHT,
    };
    const updateRendererOptions = createObjectUpdater(renderOptions.proxy);

    const updateChangedRendererOptions = (
        changed: Partial<RendererOptions>,
    ) => {
        for (const [k, v] of Object.entries(changed)) {
            //@ts-expect-error
            processorOptions.renderOptions[k] = v;
        }
    };

    const updateProcessorOptions = async (options: ProcessorUpdateOptions) => {
        const _processorOptions: ProcessorOptions = {};
        if (options?.renderOptions) {
            updateRendererOptions(options.renderOptions);
            if (!isEmpty(changedRenderOptions)) {
                changedRenderOptions = {};
                _processorOptions.renderOptions =
                    processorOptions.renderOptions;
            }
        }
        if (
            options?.backgroundImage &&
            !compareImageRecords(
                options.backgroundImage,
                processorOptions.backgroundImage,
            )
        ) {
            processorOptions.backgroundImage = options.backgroundImage;

            _processorOptions.backgroundImage =
                processorOptions.backgroundImage;
        }
        if (options.processingWidth) {
            processorOptions.processingWidth = options.processingWidth;
            _processorOptions.processingWidth =
                processorOptions.processingWidth;
        }
        if (options.processingHeight) {
            processorOptions.processingHeight = options.processingHeight;
            _processorOptions.processingHeight =
                processorOptions.processingHeight;
        }
        if (options.imageSegmenterOptions) {
            processorOptions.imageSegmenterOptions = {
                ...processorOptions.imageSegmenterOptions,
                ...options.imageSegmenterOptions,
            };
            _processorOptions.imageSegmenterOptions =
                processorOptions.imageSegmenterOptions;
        }
        if (canUpdate() && !isEmpty(_processorOptions)) {
            const changed = await segmenter.update(_processorOptions);
            updateChangedRendererOptions(changed);
        }
    };

    const open: VideoProcessor['open'] = async (
        options?: ProcessorUpdateOptions,
    ) => {
        if (options) {
            await updateProcessorOptions(options);
        }
        if (segmenter.status === 'new') {
            const changed = await segmenter.open(processorOptions);
            updateChangedRendererOptions(changed);
        }
    };

    const update: VideoProcessor['update'] = async options => {
        await updateProcessorOptions(options);
    };

    // Serialization lock: `process()` and `close()` mutate the same worker
    // pipeline (segmenter + transferred tracks). If they overlap, the worker
    // can tear down a writable stream while the main thread is still feeding
    // it, surfacing as `InvalidStateError: Stream closed`. Chain every
    // public mutation through this promise so they always run end-to-end.
    let serialized: Promise<unknown> = Promise.resolve();
    const runSerialized = <T>(fn: () => Promise<T>): Promise<T> => {
        const next = serialized.then(fn, fn);
        serialized = next.catch(() => undefined);
        return next;
    };

    const closeImpl = async () => {
        await segmenter.close();
        props.outputTrack?.stop();
        props.outputTrack = undefined;
        props.processing = false;
    };

    const processImpl = async (source: MediaStreamTrack) => {
        if (props.processing) {
            await closeImpl();
        }
        assertMediaStreamVideoTrack(source);
        if (segmenter.status === 'new') {
            await segmenter.open(processorOptions);
            // @ts-expect-error -- Segmenter status could be changed to Error if anything went wrong
            if (segmenter.status === 'Error') {
                return source;
            }
        }
        const processed = await segmenter.process(
            inputProcessAdaptor.adapt(source),
            processorOptions.renderOptions,
        );
        props.outputTrack = inputProcessAdaptor.outputTrack;
        if (processed instanceof MediaStreamTrack) {
            props.outputTrack = processed;
        } else if (!props.outputTrack) {
            props.outputTrack = source;
        }

        assert(props.outputTrack, 'Segmenter failed to generate output track');

        props.processing = true;
        return props.outputTrack;
    };

    const process: VideoProcessor['process'] = source =>
        runSerialized(() => processImpl(source));

    const close: VideoProcessor['close'] = () => runSerialized(closeImpl);

    const destroy: VideoProcessor['destroy'] = async () => {
        await close();
        processorOptions?.backgroundImage?.image?.close();
        renderOptions = initRenderOptions();
        changedRenderOptions = {};
        processorOptions = {
            renderOptions: renderOptions.original,
            processingWidth: PROCESSING_WIDTH,
            processingHeight: PROCESSING_HEIGHT,
        };
        await segmenter.destroy();
    };

    return {
        open,
        update,
        process,
        mute(muted) {
            segmenter.mute(muted);
        },
        close,
        destroy,

        get status() {
            return segmenter.status;
        },

        get renderOptions() {
            return processorOptions.renderOptions;
        },

        get processingWidth() {
            return processorOptions.processingWidth;
        },

        get processingHeight() {
            return processorOptions.processingHeight;
        },
    };
};
